import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import { TabProps, Environment } from './types';
import { createDefaultEnvironment } from './defaults';

interface EnvironmentsTabProps extends TabProps {
  availableEnvironments: string[];
}

export function EnvironmentsTab({
  config,
  onChange,
  availableEnvironments,
}: EnvironmentsTabProps) {
  const [showAdd, setShowAdd] = useState(false);

  const addedNames = new Set(config.environments.map(e => e.name));
  const addableOptions = availableEnvironments.filter(name => !addedNames.has(name));

  const handleRemove = (index: number) => {
    const updated = config.environments.filter((_, i) => i !== index);
    onChange({ ...config, environments: updated });
  };

  const handleAdd = (_: unknown, value: string | null) => {
    if (!value) return;

    let newEnv: Environment;
    if (config.environments.length > 0) {
      const source = config.environments[0];
      newEnv = {
        ...createDefaultEnvironment(),
        name: value,
        additional_apis: [...source.additional_apis],
        labels: source.labels.map(l => ({ ...l })),
        service_account_roles: [...source.service_account_roles],
      };
    } else {
      newEnv = { ...createDefaultEnvironment(), name: value };
    }

    onChange({
      ...config,
      environments: [...config.environments, newEnv],
    });
    setShowAdd(false);
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Typography variant="subtitle1">Environments</Typography>

      {availableEnvironments.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          Select a business unit to load available environments.
        </Typography>
      )}

      <List>
        {config.environments.map((env, index) => (
          <ListItem key={env.name} divider>
            <ListItemText
              primary={env.name}
              secondary={`${env.service_account_roles.length} service account role(s)`}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleRemove(index)}
                disabled={config.environments.length <= 1}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {addableOptions.length > 0 && (
        showAdd ? (
          <Autocomplete
            options={addableOptions}
            onChange={handleAdd}
            renderInput={params => (
              <TextField
                {...params}
                label="Select environment to add"
                variant="outlined"
                fullWidth
                autoFocus
              />
            )}
            onClose={() => setShowAdd(false)}
            openOnFocus
          />
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
          >
            Add Environment
          </Button>
        )
      )}
    </Box>
  );
}
